import { PenLine } from 'lucide-react';
import parse from 'html-react-parser';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '../../components/ui/dialog';
import useAuth from '../../stores/useAuth';
import { FormCompanyAddress, FormCompanyEmail, FormCompanyInformation, FormCompanyPhone, FormSign } from './FormUpdate';

export function CompanyContact() {
  const { company } = useAuth();

  return (
    <div className="border border-border rounded-lg p-4 h-max">
      <h3 className="text-base mb-4 font-semibold">Company Contact</h3>
      <div className="flex flex-col gap-4">
        {/* Row User Email */}
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Email</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.email || '-'}</p>
            <Dialog>
              <DialogTrigger>
                <span className="flex gap-1 items-center justify-center text-blue-700 text-sm">
                  <PenLine size={14} />
                  Edit
                </span>
              </DialogTrigger>
              <DialogContent className="max-w-xs md:max-w-xl rounded-md">
                <FormCompanyEmail />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Row Phone Number */}
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Phone Number</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.phone || '-'}</p>
            <Dialog>
              <DialogTrigger>
                <span className="flex gap-1 items-center justify-center text-blue-700 text-sm">
                  <PenLine size={14} />
                  Edit
                </span>
              </DialogTrigger>
              <DialogContent className="max-w-xs md:max-w-xl rounded-md">
                <FormCompanyPhone />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CompanyAddress() {
  const { company } = useAuth();
  return (
    <div className="border border-border rounded-lg p-4 h-max">
      <h3 className="text-base mb-4 font-semibold">Company Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Country</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.country || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">State</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.state || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">City</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.city || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">District</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.district || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Address Line 1</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.address_line_1 || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Address Line 2</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.address_line_2 || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Zip Code</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.zip_code || '-'}</p>
          </div>
        </div>
      </div>
      <Dialog>
        <DialogTrigger className="mt-1 w-full flex justify-end">
          <Button
            variant="outline"
            className="flex gap-1 items-center justify-center text-blue-700 text-sm border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            <PenLine size={14} />
            Edit
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-xs md:max-w-xl rounded-md">
          <FormCompanyAddress />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CompanyInformation() {
  const { company } = useAuth();
  return (
    <div className="border border-border rounded-lg p-4 h-max">
      <h3 className="text-base mb-4 font-semibold">Company Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Company Name</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.name || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Tax Identification</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.taxIdent || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm">Website</p>
          <div className="flex justify-between items-center">
            <p className="font-normal text-sm">{company?.website || '-'}</p>
          </div>
        </div>
      </div>
      <Dialog>
        <DialogTrigger className="mt-1 w-full flex justify-end">
          <Button
            variant="outline"
            className="flex gap-1 items-center justify-center text-blue-700 text-sm border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            <PenLine size={14} />
            Edit
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-xs md:max-w-xl rounded-md">
          <FormCompanyInformation />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LogoAndDocument() {
  const { company } = useAuth();

  const parsedNotes = parse(company?.notes as string);

  return (
    <div className="border border-border rounded-lg p-4 h-max">
      <h3 className="text-base mb-4 font-semibold">Logo and Document Signature</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Company Logo */}
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-sm">Company Logo</p>
          <div className="flex justify-center items-center w-full">
            {company?.logo ? (
              <img
                src={company?.logo}
                alt="Company Logo"
                width={100}
                height={100}
                className="object-cover rounded-lg border border-gray-300 shadow-sm"
              />
            ) : (
              <div className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 hover:bg-gray-100">
                <p className="text-sm text-gray-500 font-semibold">No document available</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature */}
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-sm">Signature</p>
          <div className="flex justify-center items-center w-full">
            {company?.signature ? (
              <img
                src={company?.signature}
                alt="Signature"
                width={100}
                height={50}
                className="object-contain rounded-lg border border-gray-300 shadow-sm"
              />
            ) : (
              <div className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 hover:bg-gray-100">
                <p className="text-sm text-gray-500 font-semibold">No document available</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature Name */}
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-sm">Signature Name</p>
          <div className="flex justify-start items-center">
            <p className="font-normal text-sm text-gray-700 ml-2">{company?.signature_name || '-'}</p>
          </div>
        </div>

        {/* Notes */}
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-sm">Notes</p>
          <div className="flex justify-start items-center">
            <p className="font-normal text-sm text-gray-700 ml-2">{parsedNotes || '-'}</p>
          </div>
        </div>
      </div>
      <Dialog>
        <DialogTrigger className="mt-1 w-full flex justify-end">
          <Button
            variant="outline"
            className="flex gap-1 items-center justify-center text-blue-700 text-sm border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            <PenLine size={14} />
            Edit
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-xs md:max-w-xl rounded-md">
          <FormSign />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CompanyProfile() {
  return (
    <div className="flex md:flex-row flex-col gap-4 text-text">
      {/* Column Left */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Company Contact */}
        <CompanyContact />
        {/* Company Address */}
        <CompanyAddress />
      </div>

      {/* Column Right */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Company Information */}
        <CompanyInformation />
        {/* Logo and Document */}
        <LogoAndDocument />
      </div>
    </div>
  );
}
